import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useFindUpcomingEvents } from "../../api/queries/useFindUpcomingEvents";
import { isLoggedIn } from "../../auth/authStorage";
import {
  clearExplorerJoinForceSignup,
  markExplorerJoinSelectedWhileAnonymous,
  setExplorerContext,
  type ExplorerFlow,
} from "../../explorer/explorerSession";
import {
  isEventDateStillActive,
  isPastEventDateEnd,
  parseEventDateAsLocalDay,
} from "../../utils/eventDateUtils";

const FEATURE_ICON_SIZE = { width: 48, height: 48 };

/**
 * Generic Explorer entry URL:
 * - /explorer/join (landing with active events)
 * - /explorer/join?eventId=123&flow=event (existing redirect behavior)
 */
function ExplorerJoin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get("eventId");
  const flow = (searchParams.get("flow") || "event") as ExplorerFlow;
  const {
    data: eventsResponse,
    isPending: isLoadingEvents,
    error: eventsError,
  } = useFindUpcomingEvents({ pageSize: 100, page: 1, enabled: !eventId });
  const [searchTerm, setSearchTerm] = useState("");
  /** Dev Strict Mode runs effects twice; avoid double redirect / double consume of session intent. */
  const joinEventRedirectHandledRef = useRef<string | null>(null);

  const activeEvents = useMemo(() => {
    if (!eventsResponse?.data?.length) return [];
    return eventsResponse.data
      .filter(
        (event) =>
          isEventDateStillActive(event.eventDate) &&
          !isPastEventDateEnd(event.eventDate),
      )
      .sort(
        (a, b) =>
          parseEventDateAsLocalDay(a.eventDate).getTime() -
          parseEventDateAsLocalDay(b.eventDate).getTime(),
      );
  }, [eventsResponse?.data]);

  const filteredEvents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return activeEvents;
    return activeEvents.filter((event) => {
      const haystack = `${event.name} ${event.description ?? ""} ${event.location ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [activeEvents, searchTerm]);

  const hasActiveFilter = searchTerm.trim().length > 0;

  useEffect(() => {
    if (!eventId) return;
    const dedupeKey = `${eventId}:${flow}`;
    if (joinEventRedirectHandledRef.current === dedupeKey) return;
    joinEventRedirectHandledRef.current = dedupeKey;

    setExplorerContext(eventId, flow);

    const q = new URLSearchParams();
    if (eventId) q.set("eventId", eventId);
    const entrySource =
      flow === "mentor"
        ? "mentorship"
        : flow === "mentee"
          ? "mentee"
          : flow === "venture"
            ? "venture"
            : "event";
    q.set("entrySource", entrySource);
    navigate(`/explorer/signup?${q.toString()}`, { replace: true });
  }, [eventId, flow, navigate]);

  const handleSelectEvent = (selectedEventId: number, selectedEventCode?: string | null) => {
    if (selectedEventCode?.trim()) {
      const safeCode = encodeURIComponent(selectedEventCode.trim());
      navigate(`/explorer/signup/${safeCode}?entrySource=event`);
      return;
    }
    if (isLoggedIn()) clearExplorerJoinForceSignup();
    else markExplorerJoinSelectedWhileAnonymous(String(selectedEventId));
    navigate(`/explorer/join?eventId=${selectedEventId}&flow=event`);
  };

  if (eventId) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-white">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="explorer-wizard-root explorer-event-landing-root position-relative bg-white">
      <div className="explorer-wizard-backdrop" aria-hidden />
      <div className="explorer-wizard-stack explorer-event-landing-stack mx-auto">
        <div className="explorer-wizard-card explorer-event-landing-card">
          <div className="explorer-wizard-body explorer-event-landing-body">
            <header className="explorer-landing-site-header">
              <div className="explorer-landing-brand">
                <img src="/connect-logo.png" alt="SMBF logo" />
              </div>
             
              <div className="explorer-landing-header-actions">
              
                <a
                  href="#explorer-events"
                  className="btn bg-project-gradient border-0 rounded-3 px-5 py-2 fw-semibold text-white"
                >
                  Join Now
                </a>
              </div>
            </header>
            <section className="row g-3 g-lg-4 mb-3">
              <div className="col-md-4">
                <article className="card border border-light shadow-sm rounded-3 h-100 bg-white">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0"
                        style={FEATURE_ICON_SIZE}
                        aria-hidden
                      >
                        <i className="bi bi-people fs-5" />
                      </div>
                      <div>
                        <div className="fw-bold">Community Reach</div>
                        <div className="fw-bold mb-1">Growing Community</div>
                        <p className="text-muted small mb-0">
                          Founders, mentors, and operators actively sharing opportunities.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
              <div className="col-md-4">
                <article className="card border border-light shadow-sm rounded-3 h-100 bg-white">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                        style={FEATURE_ICON_SIZE}
                        aria-hidden
                      >
                        <i className="bi bi-chat-dots fs-5" />
                      </div>
                      <div>
                        <div className="fw-bold">Live Conversations</div>
                        <div className="fw-bold mb-1">Weekly Networking</div>
                        <p className="text-muted small mb-0">
                          Curated sessions that help members connect and collaborate quickly.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
              <div className="col-md-4">
                <article className="card border border-light shadow-sm rounded-3 h-100 bg-white">
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center flex-shrink-0"
                        style={FEATURE_ICON_SIZE}
                        aria-hidden
                      >
                        <i className="bi bi-globe2 fs-5" />
                      </div>
                      <div>
                        <div className="fw-bold">Global Visibility</div>
                        <div className="fw-bold mb-1">12+ Countries</div>
                        <p className="text-muted small mb-0">
                          Cross-border exposure through trusted Muslim business circles.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>

            <div className="mb-3" id="explorer-events">
              <h2 className="fs-3 fw-bold text-dark mb-2 lh-sm">
                Choose your event to continue
              </h2>
              <p className="text-secondary mb-4">
                Pick an active event below and proceed directly to signup.
              </p>
              {!isLoadingEvents && !eventsError && activeEvents.length > 0 ? (
                <div className="explorer-event-landing-toolbar">
                  <div className="input-group border border-secondary-subtle rounded-3 shadow-sm overflow-hidden bg-white">
                    <span className="input-group-text bg-white border-0 ps-3 pe-2 text-secondary">
                      <i className="bi bi-search" aria-hidden />
                    </span>
                    <input
                      type="text"
                      className="form-control border-0 bg-white py-3 ps-1 text-secondary"
                      placeholder="Search events by name, description, or location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      aria-label="Search active events"
                    />
                  </div>
                  {hasActiveFilter ? (
                    <div className="explorer-event-toolbar-note">
                      Showing {filteredEvents.length} of {activeEvents.length}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {isLoadingEvents ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading active events…</span>
                </div>
              </div>
            ) : eventsError ? (
              <div className="alert alert-danger mb-0" role="alert">
                We could not load active events right now. Please try again shortly.
              </div>
            ) : activeEvents.length === 0 ? (
              <div className="alert alert-info mb-0" role="alert">
                There are no active events at the moment.
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="alert alert-secondary mb-0" role="alert">
                No events match your search. Try a different keyword.
              </div>
            ) : (
              <div className="row g-3 g-lg-4">
                {filteredEvents.map((event) => (
                  <div className="col-md-4" key={event.id}>
                    <article className="card border border-light shadow-sm h-100 rounded-3 bg-white">
                      <div className="card-body p-4 d-flex flex-column">
                        <h3 className="h5 fw-bold text-dark mb-1">{event.name}</h3>
                        <p className="text-muted small mb-3 text-truncate">
                          {event.description || "Event"}
                        </p>
                        <div className="d-flex flex-wrap align-items-center gap-3 text-secondary small mb-4">
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-calendar3 flex-shrink-0" aria-hidden />
                            <span>
                              {parseEventDateAsLocalDay(event.eventDate).toLocaleDateString(
                                undefined,
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-geo-alt flex-shrink-0" aria-hidden />
                            <span>{event.location || "TBC"}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn explorer-wizard-btn explorer-event-card-cta bg-project-gradient border-0 rounded-3 w-100 mt-auto"
                          onClick={() => handleSelectEvent(event.id, event.eventCode)}
                        >
                          Continue with this event
                          <i className="bi bi-arrow-right ms-1" aria-hidden />
                        </button>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExplorerJoin;
